import type { BoardState, MatchGroup, Position, SpecialKind } from "./types";

export interface SpecialSpawn {
  at: Position;
  special: SpecialKind;
}

export interface BombSeed {
  center: Position;
  special: "BOMB_3" | "BOMB_4" | "BOMB_5";
}

export type WipeColorSelection =
  | {
      kind: "FIXED_COLORS";
      colors: number[];
    }
  | {
      kind: "RANDOM_VISIBLE_COLORS";
      count: 1 | 2;
    };

export interface WipeSwapPayload {
  activators: Position[];
  selection: WipeColorSelection;
}

export interface SpecialSwapActivation {
  triggered: boolean;
  wipe: WipeSwapPayload | null;
  bombSeeds: BombSeed[];
}

export function isBomb(special: SpecialKind): special is "BOMB_3" | "BOMB_4" | "BOMB_5" {
  return special === "BOMB_3" || special === "BOMB_4" || special === "BOMB_5";
}

export function isWipe(special: SpecialKind): boolean {
  return special === "WIPE";
}

export function collectEligibleWipeColors(board: BoardState): number[] {
  const colors = new Set<number>();
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const cell = board.cells[row][col];
      if (
        cell.kind === "TILE"
        && cell.tile != null
        && cell.tile.special === "NONE"
      ) {
        colors.add(cell.tile.color);
      }
    }
  }
  return [...colors].sort((a, b) => a - b);
}

export function activationFromSwap(
  board: BoardState,
  a: Position,
  b: Position
): SpecialSwapActivation {
  const cellA = board.cells[a.row][a.col];
  const cellB = board.cells[b.row][b.col];
  const specialA = cellA.kind === "TILE" && cellA.tile != null ? cellA.tile.special : "NONE";
  const specialB = cellB.kind === "TILE" && cellB.tile != null ? cellB.tile.special : "NONE";

  let triggered = false;
  let wipe: WipeSwapPayload | null = null;
  const bombSeeds: BombSeed[] = [];

  if (
    isWipe(specialA)
    && isWipe(specialB)
  ) {
    wipe = {
      activators: [
        { row: a.row, col: a.col },
        { row: b.row, col: b.col }
      ],
      selection: {
        kind: "RANDOM_VISIBLE_COLORS",
        count: 2
      }
    };
    triggered = true;
  } else if (
    isWipe(specialA)
    && cellB.kind === "TILE"
    && cellB.tile != null
    && cellB.tile.special === "NONE"
  ) {
    wipe = {
      activators: [{ row: b.row, col: b.col }],
      selection: {
        kind: "FIXED_COLORS",
        colors: [cellB.tile.color]
      }
    };
    triggered = true;
  } else if (
    isWipe(specialB)
    && cellA.kind === "TILE"
    && cellA.tile != null
    && cellA.tile.special === "NONE"
  ) {
    wipe = {
      activators: [{ row: a.row, col: a.col }],
      selection: {
        kind: "FIXED_COLORS",
        colors: [cellA.tile.color]
      }
    };
    triggered = true;
  }

  if (isBomb(specialA)) {
    bombSeeds.push({
      center: { row: b.row, col: b.col },
      special: specialA
    });
    triggered = true;
  }
  if (isBomb(specialB)) {
    bombSeeds.push({
      center: { row: a.row, col: a.col },
      special: specialB
    });
    triggered = true;
  }

  return {
    triggered,
    wipe,
    bombSeeds
  };
}

export function specialFromMatch(group: MatchGroup, level: number): SpecialKind {
  const levelNum = Math.max(1, Math.floor(level));
  if (group.shape === "T") {
    return "WIPE";
  }
  const bomb5Threshold = levelNum >= 10 ? 4 : 5;
  const wipeLineThreshold = levelNum >= 10 ? 5 : 6;
  const wipeShapeThreshold = levelNum >= 10 ? 4 : 5;
  if (group.hasIntersection && group.maxSpecialRunLength >= bomb5Threshold) {
    return "BOMB_5";
  }
  if (group.maxSpecialRunLength >= wipeLineThreshold) {
    return "WIPE";
  }
  if (
    levelNum >= 5
    &&
    (group.shape === "L" || group.shape === "COMPOSITE")
    && group.maxSpecialRunLength >= wipeShapeThreshold
  ) {
    return "WIPE";
  }
  if (group.maxSpecialRunLength === 5) {
    return "BOMB_4";
  }
  if (group.maxSpecialRunLength === 4) {
    return "BOMB_3";
  }
  return "NONE";
}

export function pickSpawnCell(group: MatchGroup, preferred?: Position): Position {
  if (preferred != null) {
    const match = group.cells.find((cell) => cell.row === preferred.row && cell.col === preferred.col);
    if (match != null) {
      return match;
    }
  }
  const sorted = [...group.cells].sort((a, b) => {
    if (a.row !== b.row) return a.row - b.row;
    return a.col - b.col;
  });
  return sorted[Math.floor(sorted.length / 2)];
}
